import React, { useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useSidebarState } from '../../hooks/useSidebarState';
import { useLanguage } from '../../contexts/LanguageContext';
import { InstallPrompt } from '../common/InstallPrompt';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import toast from 'react-hot-toast';
import { ReloadPrompt } from '../common/ReloadPrompt';
import { GuideAgreementGate } from '../guides/GuideAgreementGate';
import { useSettingsStore } from '../../stores/settingsStore';
import { signOut } from '../../lib/firebase/auth';
import { getOrganizerAccessErrorMessage, isWithinOrganizerAccessWindow } from '../../utils/organizerAccess';

type Props = {
  children: React.ReactNode;
};

export const Layout = ({ children }: Props) => {
  const { isCollapsed } = useSidebarState();
  const { direction } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchOrganizerAccessWindow } = useSettingsStore();

  useEffect(() => {
    const checkVerificationStatus = async () => {
      const userRole = localStorage.getItem('userRole');
      const userId = localStorage.getItem('userId');
      
      // Only check for organizers
      if (!userId || userRole !== 'organizer') return;
      
      // Allow access to verification wizard, login and root pages
      if (
        location.pathname === '/verification-wizard' ||
        location.pathname === '/login' ||
        location.pathname === '/' ||
        location.pathname === '/guides' ||
        location.pathname === '/settings' ||
        location.pathname.startsWith('/verification-details/') // Also allow access to verification details
      ) return;

      try {
        const verificationsRef = collection(firestore, 'verifications');
        const q = query(
          verificationsRef,
          where('userId', '==', userId)
        );
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          // No verification found, redirect to wizard
          navigate('/verification-wizard');
          return;
        }

        const verification = querySnapshot.docs[0].data();
        if (verification.status !== 'approved') {
          // Verification exists but not approved
          navigate('/verification-wizard');
          toast.error('Your verification is pending approval. Access is limited.');
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };

    checkVerificationStatus();
  }, [navigate, location.pathname]);

  useEffect(() => {
    let intervalId: number | undefined;
    let isActive = true;

    const checkOrganizerAccess = async () => {
      const userRole = localStorage.getItem('userRole');
      const userId = localStorage.getItem('userId');

      if (!userId || userRole !== 'organizer') return;

      const storedWindow = useSettingsStore.getState().organizerAccessWindow;
      const accessWindow = storedWindow ?? await fetchOrganizerAccessWindow();

      if (!isWithinOrganizerAccessWindow(accessWindow)) {
        if (!isActive) return;
        await signOut();
        toast.error(getOrganizerAccessErrorMessage(accessWindow));
        navigate('/login', { replace: true });
      }
    };

    checkOrganizerAccess();
    intervalId = window.setInterval(checkOrganizerAccess, 60_000);

    return () => {
      isActive = false;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [fetchOrganizerAccessWindow, navigate]);

  return (
    <div className="min-h-screen bg-gray-50" dir={direction}>
      <Sidebar />
      <div 
        className={`
          transition-all duration-300
          ${direction === 'rtl'
            ? isCollapsed 
              ? 'lg:mr-20'  // 80px when collapsed
              : 'lg:mr-72'  // 288px when expanded
            : isCollapsed
              ? 'lg:ml-20'  // 80px when collapsed
              : 'lg:ml-72'  // 288px when expanded
          }
        `}
      >
        <Header />
        <main className="p-4 lg:p-6">
          <div className={`
            mx-auto w-full 
            lg:${isCollapsed ? 'max-w-[calc(100vw-80px)]' : 'max-w-[calc(100vw-288px)]'}
            px-4
          `}>
            {children}
          </div>
        </main>
      </div>
      <InstallPrompt />
      <ReloadPrompt />
      <GuideAgreementGate />
    </div>
  );
};
